<script setup>
import {
  computed,
  defineAsyncComponent,
  onBeforeMount,
  onMounted,
  ref,
  watch
} from 'vue'
import { useRoute, useRouter } from 'vue-router'

import IconSetCritical from '@/components/basic/atoms/IconSetCritical.vue'
import IconSetHeart from '@/components/basic/atoms/IconSetHeart.vue'
import Overlay from '@/components/basic/atoms/Overlay.vue'
import { useGlobals } from '@/composables/useGlobals.js'
import SiteConfig from '@/services/SiteConfig.js'
import { useCmsBlocks } from '@/stores/cmsBlocks.js'
import { useCmsVariables } from '@/stores/cmsVariables.js'
import { useCollectionPageStore } from '@/stores/collectionPage.js'
import { useCustomerStore } from '@/stores/customer.js'
import { useDoiOptInStore } from '@/stores/doiOptIn'
import { useFeaturesStore } from '@/stores/features.js'
import { useHead } from '@unhead/vue'
import { storeToRefs } from 'pinia'
import { useDisplay, useTheme } from 'vuetify'
import TheAuthentication from './components/basic/organisms/TheAuthentication.vue'
import { useAccessibilityStore } from './stores/accessibility.js'
import { useCartStore } from './stores/cart.js'
import { useConsentStore } from './stores/consent.js'

const ThePopoverWrapper = defineAsyncComponent(
  () => import('@/components/basic/molecules/ThePopoverWrapper.vue')
)
const TheCountrySwitcher = defineAsyncComponent(
  () => import('@/components/basic/organisms/TheCountrySwitcher.vue')
)
const AlertBanner = defineAsyncComponent(
  () => import('@/components/basic/atoms/AlertBanner.vue')
)
const TheDialog = defineAsyncComponent(
  () => import('@/components/basic/organisms/TheDialog.vue')
)
const TheDoiConfirmation = defineAsyncComponent(
  () => import('@/components/basic/organisms/TheDoiConfirmation.vue')
)
const AccessibilityButton = defineAsyncComponent(
  () => import('@/components/basic/atoms/AccessibilityButton.vue')
)
const CustomerTools = defineAsyncComponent(
  () => import('@/components/basic/atoms/CustomerTools.vue')
)
const getModules = (() => {
  let cache

  return async () => {
    if (cache) return cache

    const { default: CouponHelper } =
      await import('@/services/helpers/CouponHelper')
    cache = { CouponHelper }
    return cache
  }
})()

const { translate } = useGlobals()
const customer = useCustomerStore()
const collectionPageStore = useCollectionPageStore()
const blocks = useCmsBlocks()
const variables = useCmsVariables()
const features = useFeaturesStore()
const display = useDisplay()
const theme = useTheme()
const accessibility = useAccessibilityStore()
const doiOptIn = useDoiOptInStore()

const consent = useConsentStore()
// Consent events and the high-contrast toggle are browser state — during a
// server render there is no window/localStorage and the default theme applies
// (VIP-12037)
if (!import.meta.env.SSR) {
  consent.initializeConsentListeners()
}

const HIGH_CONTRAST_TOGGLE = 'show_high_contrast'
const themeName =
  !import.meta.env.SSR && localStorage.getItem(HIGH_CONTRAST_TOGGLE)
    ? 'vicampoThemeHighContrast'
    : 'vicampoThemeRebrand'

if (typeof theme.change === 'function') {
  theme.change(themeName)
} else {
  theme.global.name.value = themeName
}

const {
  customerProfile,
  isLoginShowing,
  showAdminPermissionError,
  customerIsAdmin
} = storeToRefs(customer)
const { redirectPreview } = storeToRefs(collectionPageStore)
const { doiConfirmation } = storeToRefs(doiOptIn)

const isDoiConfirmationShowing = computed({
  get: () => !!doiConfirmation.value,
  set: (visible) => {
    if (!visible) doiOptIn.dismissDoiConfirmation()
  }
})

// Admin-only warning: this collection page would redirect a customer away.
const showRedirectPreview = computed(
  () => customerIsAdmin.value && !!redirectPreview.value
)

const route = useRoute()
// The injected router — during SSR that is the request's own instance, not
// the shared module singleton (VIP-12037)
const router = useRouter()

const countrySwitcherData = ref(null)

const isLoggedInAsCustomer = ref(false)

// Function to check cookie and update reactive value
const checkCookie = async () => {
  if (typeof document !== 'undefined') {
    const { default: CookieHelper } =
      await import('@/services/helpers/CookieHelper')
    const cookieValue = CookieHelper.getCookie('original-customer-id')
    isLoggedInAsCustomer.value = !!cookieValue
  } else {
    isLoggedInAsCustomer.value = false
  }
}

// Check on mount and when route changes
onMounted(async () => {
  await checkCookie()
})

// Watch for route changes to check cookie
watch(
  () => route.path,
  async () => {
    await checkCookie()
  }
)

const alertBannerText = computed(() => {
  if (showAdminPermissionError.value) {
    return translate('fe_app_admin_permission_required')
  }
  if (showRedirectPreview.value) {
    const reasons = (redirectPreview.value.reasons || [])
      .map((reason) => translate('fe_app_collection_redirect_reason_' + reason))
      .join(', ')
    return translate(
      'fe_app_collection_redirect_preview',
      redirectPreview.value.url,
      reasons
    )
  }
  if (isLoggedInAsCustomer.value) {
    return translate('fe_app_logged_in_as', customerProfile.value?.email)
  }

  return ''
})

const countrySwitcherTitle = computed(() => {
  const switchTo = translate(
    'fe_app_country_' + countrySwitcherData.value?.siteId
  )
  return translate('fe_app_country_switcher_header', switchTo)
})

const displayClassUp = computed(() => {
  const width = display.width.value
  const thresholds = display.thresholds.value

  switch (true) {
    case width >= thresholds.xxl:
      return 'display-xxl-and-up display-xl-and-up display-lg-and-up display-md-and-up display-sm-and-up display-xs-and-up'
    case width >= thresholds.xl:
      return 'display-xl-and-up display-lg-and-up display-md-and-up display-sm-and-up display-xs-and-up'
    case width >= thresholds.lg:
      return 'display-lg-and-up display-md-and-up display-sm-and-up display-xs-and-up'
    case width >= thresholds.md:
      return 'display-md-and-up display-sm-and-up display-xs-and-up'
    case width >= thresholds.sm:
      return 'display-sm-and-up display-xs-and-up'
    case width >= thresholds.xs:
      return 'display-xs-and-up'
    default:
      return ''
  }
})

const updateShowLogin = (visible) => {
  customer.showLogin(visible)
}

onBeforeMount(async () => {
  await customer.establishSession()

  void variables.fetchDefaults()
  void blocks.fetchDefaults()
  void features.refresh()

  // Gatekeeper flags are customer-specific: refetch them whenever the session
  // identity changes (login, 2FA, registration, logout). Registered after
  // establishSession so it only fires on real changes, not on boot. bustCache
  // skips the service worker's hour-long CacheFirst copy, which still belongs
  // to the previous identity.
  watch(
    () => customerProfile.value?.customer_id,
    () => features.refresh({ bustCache: true })
  )

  const [{ default: TagManager }, { default: SiteConfig }] = await Promise.all([
    await import('@/services/analytics/TagManager'),
    await import('@/services/SiteConfig')
  ])

  TagManager.pushToDataLayer({ application: 'fe-app' })
  countrySwitcherData.value = await SiteConfig.offerOtherSite()

  // refresh session as soon as the tab becomes active again
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return
    customer.establishSession()
  })
})

onMounted(async () => {
  addTrustedShopsBadge()

  accessibility.updateCSSVariables()

  // Browser-only lib, loaded on demand so it stays out of the shared module
  // graph (VIP-12036)
  const { default: SmoothScroll } =
    await import('smooth-scroll/dist/smooth-scroll.min.js')
  SmoothScroll('a[href*="#"]', {
    speed: 500,
    speedAsDuration: true,
    header: '.navbar > div',
    easing: 'easeInOutCubic',
    updateURL: false
  })
})

useHead({
  titleTemplate: `${SiteConfig.devMode ? '[ 👾] ' : ''}%s | ${SiteConfig.name} `
})

const addTrustedShopsBadge = () => {
  const siteIdToTsIdMap = {
    de: import.meta.env.VITE_TRUSTED_SHOPS_ID_VIC_DE,
    at: import.meta.env.VITE_TRUSTED_SHOPS_ID_VIC_AT,
    ch: import.meta.env.VITE_TRUSTED_SHOPS_ID_VIC_CH
  }

  const locale = SiteConfig.locale
  const tsId = siteIdToTsIdMap[locale]

  if (!tsId) return

  const tsScript = document.createElement('script')
  tsScript.async = true
  tsScript.src = `//widgets.trustedshops.com/js/${tsId}.js`

  tsScript.dataset.desktopYOffset = '0'
  tsScript.dataset.mobileYOffset = '0'
  tsScript.dataset.desktopDisableReviews = 'false'
  tsScript.dataset.desktopEnableCustom = 'false'
  tsScript.dataset.desktopPosition = 'left'
  tsScript.dataset.desktopCustomWidth = '156'
  tsScript.dataset.desktopEnableFadeout = 'true'
  tsScript.dataset.disableMobile = 'false'
  tsScript.dataset.disableTrustbadge = 'false'
  tsScript.dataset.mobileCustomWidth = '156'
  tsScript.dataset.mobileDisableReviews = 'false'
  tsScript.dataset.mobileEnableCustom = 'false'
  tsScript.dataset.mobilePosition = 'left'
  tsScript.dataset.mobileEnableTopbar = 'false'
  tsScript.dataset.mobileEnableFadeout = 'true'
  tsScript.dataset.colorScheme = 'dark'

  document.body.appendChild(tsScript)
  toggleTrustBadgeVisibility()
}

const toggleTrustBadgeVisibility = () => {
  const trustBadge = document.querySelector('[id^="trustbadge-container-"]')
  if (!trustBadge) return

  const { pathname } = window.location
  // The badge floats bottom-left over the page, where it covers the account content.
  const shouldHide = /checkout\/v2|pdp|cms|cat|account\/manage/.test(pathname)

  trustBadge.classList.toggle('d-none', shouldHide)
}

// Coupon/cart refresh and the DOI popup are browser flows; registering them
// during a server render would grow the router's handler list with every
// request (VIP-12037)
if (!import.meta.env.SSR) {
  router.afterEach(async () => {
    toggleTrustBadgeVisibility()

    const { CouponHelper } = await getModules()
    const cart = useCartStore()

    const { storedCouponCode } = storeToRefs(cart)

    const coupon = await CouponHelper.checkForCoupon()
    if (coupon !== null && coupon !== storedCouponCode) {
      await cart.applyCouponCode(coupon)
    } else {
      await cart.refreshCart()
    }
  })

  // Deliberately a second hook: the newsletter confirmation landing must not be
  // able to break the coupon/cart refresh above. afterEach and not onMounted
  // because api-v2 decides the redirect target server-side, so the popup cannot
  // hang off one particular page component.
  router.afterEach(async () => {
    const { default: DoiConfirmationHelper } =
      await import('@/services/helpers/DoiConfirmationHelper')

    await DoiConfirmationHelper.checkForDoiConfirmation()
  })
}
</script>
<template>
  <VApp id="root" :class="displayClassUp">
    <AlertBanner
      v-if="
        isLoggedInAsCustomer || showAdminPermissionError || showRedirectPreview
      "
      :text="alertBannerText"
    />
    <RouterView />
    <Overlay />
    <IconSetHeart />
    <IconSetCritical />
    <TheDialog
      v-if="countrySwitcherData"
      :title="countrySwitcherTitle"
      v-model="countrySwitcherData"
      @close="countrySwitcherData = null"
    >
      <TheCountrySwitcher
        :country-switcher-data="countrySwitcherData"
        @close="countrySwitcherData = null"
      />
    </TheDialog>
    <TheDialog v-model="isLoginShowing" @close="updateShowLogin(false)">
      <TheAuthentication
        :allowed-actions="['login', 'forgot-password', 'two-factor']"
        :message="translate('disclaimer_you_own_already_an_account')"
        :message-title="
          translate('disclaimer_you_own_already_an_account_title')
        "
        @success="updateShowLogin(false)"
      />
    </TheDialog>
    <TheDialog
      v-if="doiConfirmation"
      v-model="isDoiConfirmationShowing"
      disable-mobile-fullscreen
      max-width="840"
      @close="doiOptIn.dismissDoiConfirmation()"
    >
      <TheDoiConfirmation
        :status="doiConfirmation"
        :voucher-code="doiOptIn.doiVoucherCode"
        @close="doiOptIn.dismissDoiConfirmation()"
      />
    </TheDialog>
    <ThePopoverWrapper :show-nps="false" />
    <CustomerTools>
      <AccessibilityButton />
    </CustomerTools>
  </VApp>
</template>
